import React from "react";
import Loader from "Components/loader/Loader";
import Toggle from "Components/toggle/Toggle";
import Button from "Components/button/Button";
import WarningBanner from "Components/warningBanner/WarningBanner";
import TranslatedString from "Components/TranslatedString";
import RichEditor from "Components/richEditor/RichEditor";
import Select from "Components/select/Select";
import t from "Utils/translation";
import cx from "classnames";
import s from "./generals.scss";
import DropdownIndicator from "Components/customArrowSelect/CustomArrowSelect";
import QboxmailApi from "@/qboxmailApi";
import { Icon, warningFillYellow } from "Icons/index";
import { alertError, alertSuccess } from "Utils/alerts";
import { bytesToHuman } from "@/utils/bytesFormats";
import Modal from "Components/smallModal/smallModal";
import AvailabilityBox from "Components/availabilityBox/AvailabilityBox";

import { signatureBytesLimit, tzs, getWebmailLangs, webmailTimeFormats, webmailDateFormats } from "@/config";

export default class WebmailSettings extends React.Component {
  constructor(props) {
    super(props);

    this.editorRef = React.createRef();
    this.api = new QboxmailApi({ useCookie: true });

    this.state = {
      viewFields: [
        "webmail_language",
        "webmail_time_format",
        "webmail_date_format",
        "webmail_timezone",
        "hide_from_addressbook_company",
        "apply_signature_new_emails",
        "webmail_signature_template",
      ],
      noSignatureEmails: [],
      missingFieldModalOpened: false,
      applyToMissingOpened: false,
      missingFieldSelected: null,
      availableVars: [],
      isApplying: false,
      missing: {},
      touched: false,
    };
  }

  async componentDidMount() {
    // set content to RichEditor
    this.initRichEditor();

    this.api.getDomainSignatureTemplates(this.props.domain.code).then((res) => {
      this.setState({
        availableVars: res.available_vars,
        missing: res.missing,
      });
    });
  }

  componentDidUpdate(prevProps) {
    if (prevProps.formData.webmail_signature_template !== this.props.formData.webmail_signature_template) {
      this.initRichEditor();
    }
  }

  initRichEditor = () => {
    this.editorRef.current.setContent(this.props.formData.webmail_signature_template);
  };

  componentWillUnmount() {
    this.props.resetFields(...this.state.viewFields);
  }

  showMissingFieldModal = (field) => {
    this.setState({ missingFieldModalOpened: true, missingFieldSelected: field });
  };

  hideMissingFieldModal = () => this.setState({ missingFieldModalOpened: false, missingFieldSelected: false });

  submit = async (e, silent = false) => {
    // manually set value of the uncontrolled editor
    const htmlSignture = this.editorRef.current.getContent().html;

    this.props.setFormFieldValue({ webmail_signature_template: htmlSignture }, () => {
      this.props.submitForm(e, this.state.viewFields, silent);
    });
  };

  apply = async (e, toAll) => {
    e.preventDefault();

    this.setState({ isApplying: true });

    // if signature is touched save it before
    if (this.state.touched) {
      await this.submit(e, true);
    }

    try {
      await this.api.applySignatureTemplate(this.props.domain.code, {
        to_all: toAll,
      });

      this.hideApplyToAllModal();
      this.hideNoSignatureModal();
      alertSuccess(t("alert.operationSuccess"));

      this.props.forceFormAsPristine();
    } catch (err) {
      console.log("Error applying signature", err);
      alertError(t("alert.operationError"));
    }

    this.setState({ isApplying: false });
  };

  touch = () => {
    this.setState({ touched: true });
  };

  hideApplyToAllModal = () => {
    this.setState({ applyToAllOpened: false });
  };

  hideNoSignatureModal = () => {
    this.setState({ applyToMissingOpened: false });
  };

  validateSize = () => {
    const htmlSignture = this.editorRef.current.getContent().html;

    if (htmlSignture.length > signatureBytesLimit) {
      window.alert(
        t("signature.maxSize", null, { size: bytesToHuman(htmlSignture.length) }) + bytesToHuman(signatureBytesLimit)
      );
      throw new Error("Max size exceeded");
    }
  };

  showApplyToAllModal = () => {
    try {
      this.validateSize();
    } catch (e) {
      return;
    }

    this.setState({ applyToAllOpened: true });
  };

  showApplyToMissing = (e) => {
    try {
      this.validateSize();
    } catch (e) {
      return;
    }

    e.preventDefault();

    this.setState({ noSignatureLoading: true });

    this.api
      .getEmailsWithoutSignature(this.props.domain.code)
      .then((res) => {
        this.setState({ noSignatureEmails: res.no_signature });

        console.log(res);
      })
      .catch((err) => {
        console.log(err);
        console.log("Error");
      })
      .finally(() => {
        this.setState({ noSignatureLoading: false });
      });

    this.setState({ applyToMissingOpened: true });
  };

  render() {
    const { isDomainLoading, formData, formErrors, setFormFieldValue, isFormLoading, loggedUser, domain } = this.props;
    const {
      missingFieldModalOpened,
      missingFieldSelected,
      missing,
      isApplying,
      applyToMissingOpened,
      noSignatureLoading,
      noSignatureEmails,
      applyToAllOpened,
    } = this.state;

    if (isDomainLoading) {
      return <Loader />;
    }

    return (
      <form className={s.general} method="post" onSubmit={this.submit}>
        <div className={s.wrapInfoBtn}>
          <div className={s.warnignMsg}>
            {!this.props.areFieldsPristine(...this.state.viewFields) && (
              <WarningBanner color="yellow-text" msg={<TranslatedString>infoMsg.unsaveChanged</TranslatedString>} />
            )}
          </div>
          <div className={s.infoBtnGroup}>
            <Button docsKey="domains_settings" color="docs" type="button">
              button.docs
            </Button>
            <Button type="submit" loading={isFormLoading}>
              button.save
            </Button>
          </div>
        </div>
        <div className={s.settings}>
          <div className="sectionSP">
            <div className="infoSectionSP">
              <div className="settingTitle">{t("settings.sections.settingsWebmail.title")}</div>
              <div className="sectionDesc">{t("settings.sections.settingsWebmail.desc")}</div>
            </div>
            <div className="formsSectionSP">
              <Select
                label={t("input.label.language")}
                components={{ DropdownIndicator }}
                name="webmail_language"
                errors={formErrors.webmail_language}
                options={getWebmailLangs()}
                onChange={setFormFieldValue}
                value={formData.webmail_language}
                clearable={false}
              />

              <Select
                label={t("input.label.timezone")}
                components={{ DropdownIndicator }}
                name="webmail_timezone"
                errors={formErrors.webmail_timezone}
                options={tzs}
                initialValue={loggedUser.webmail_timezone}
                clearable={false}
                onChange={setFormFieldValue}
                value={formData.webmail_timezone}
              />

              <Select
                label={t("input.label.dateFormats")}
                components={{ DropdownIndicator }}
                name="webmail_date_format"
                errors={formErrors.webmail_date_format}
                options={webmailDateFormats}
                onChange={setFormFieldValue}
                value={formData.webmail_date_format}
                clearable={false}
              />

              <Select
                label={t("input.label.timeFormats")}
                components={{ DropdownIndicator }}
                name="webmail_time_format"
                errors={formErrors.webmail_time_format}
                options={webmailTimeFormats}
                initialValue={loggedUser.webmail_time_format}
                clearable={false}
                onChange={setFormFieldValue}
                value={formData.webmail_time_format}
              />
            </div>
          </div>

          {/* Signatures */}
          <div className="sectionSP">
            <div className="infoSectionSP">
              <div className="settingTitle">{t("settings.sections.settingsWebmail.signature.title")}</div>
              <div
                className="sectionDesc"
                dangerouslySetInnerHTML={{
                  __html: t("settings.sections.settingsWebmail.signature.desc"),
                }}
              ></div>
            </div>

            {/* Modal missing tags */}
            <Modal
              title={t("modal.settingsWebmail.signature.missingField.title")}
              icon={warningFillYellow}
              opened={missingFieldModalOpened}
              closeModal={this.hideMissingFieldModal}
            >
              <div>
                <p>
                  {t("modal.settingsWebmail.signature.missingField.desc.1") + " "}
                  <code>{t("settings.sections.settingsWebmail.signature.field-" + missingFieldSelected)}</code>:
                </p>
                <ul className="tableList">
                  {missingFieldSelected &&
                    missing[missingFieldSelected] &&
                    missing[missingFieldSelected].map((em) => <li key={em}>{em}</li>)}
                </ul>
                <p>{t("modal.settingsWebmail.signature.missingField.desc.2")}</p>
              </div>
              <div>
                <Button onClick={this.hideMissingFieldModal}>button.understood</Button>
              </div>
            </Modal>

            {/* Modal apply signature to all */}
            <Modal
              className={s.confirmModal}
              title={t("modal.settingsWebmail.signature.applyToAll.title")}
              icon={warningFillYellow}
              iconClassName={s.icon}
              opened={applyToAllOpened}
              closeModal={this.hideApplyToAllModal}
            >
              <div>
                <TranslatedString>modal.settingsWebmail.signature.applyToAll.desc</TranslatedString>
              </div>
              <div className={s.footer}>
                <Button type="button" color="outline" onClick={this.hideApplyToAllModal}>
                  button.close
                </Button>

                <Button loading={isApplying} onClick={(e) => this.apply(e, true)}>
                  {this.state.touched ? "button.saveApply" : "button.save"}
                </Button>
              </div>
            </Modal>

            {/* Modal apply to ones without signature modal */}
            <Modal
              title={t("modal.settingsWebmail.signature.applyTo.title")}
              icon={warningFillYellow}
              opened={applyToMissingOpened}
              closeModal={this.hideNoSignatureModal}
            >
              <div>
                {noSignatureLoading && <Loader />}

                {!noSignatureLoading && noSignatureEmails.length === 0 && (
                  <p>{t("modal.settingsWebmail.signature.applyTo.desc.1")}</p>
                )}

                {!noSignatureLoading && noSignatureEmails.length > 0 && (
                  <p>{t("modal.settingsWebmail.signature.applyTo.desc.2")}</p>
                )}

                <ul className="tableList">
                  {noSignatureEmails.map((em) => (
                    <li>{em.email_address}</li>
                  ))}
                </ul>
              </div>
              <div>
                <Button type="button" color="outline" onClick={this.hideNoSignatureModal}>
                  button.close
                </Button>

                <Button
                  loading={isApplying}
                  disabled={noSignatureEmails.length === 0}
                  onClick={(e) => this.apply(e, false)}
                >
                  {this.state.touched ? "button.saveApply" : "button.save"}
                </Button>
              </div>
            </Modal>

            <div className="formsSectionSP">
              <AvailabilityBox currentPlan={domain.plan.name} notAvailableFor={["basic"]}>
                <div className={cx("inputFull2", s.listInput)}>
                  <RichEditor
                    label={t("input.label.signature.text")}
                    ref={this.editorRef}
                    onChange={this.touch}
                    pasteOnlyText
                    disabled={domain.plan.name === "basic"}
                    maxImageBytes={150000}
                  />
                </div>

                <div className="inputFull2">
                  <h3 className={cx("settingSubTitle", { disabled: domain.plan.name === "basic" })}>
                    {t("settings.sections.settingsWebmail.signature.subtitle")}
                  </h3>
                  <p className={cx("settingText", { disabled: domain.plan.name === "basic" })}>
                    <TranslatedString
                      subs={{
                        icon: warningFillYellow,
                      }}
                    >
                      settings.sections.settingsWebmail.signature.subdesc
                    </TranslatedString>
                  </p>
                </div>

                <div
                  className={cx("inputFull2 inputAddMargin infoTableSP", { disabled: domain.plan.name === "basic" })}
                >
                  {this.state.availableVars &&
                    Object.keys(this.state.availableVars).map((tag) => {
                      let label = this.state.availableVars[tag];

                      let hasMissing = missing[label] && missing[label].length > 0;

                      return (
                        <div key={tag}>
                          <span>{`{{${tag}}}`}</span>
                          {hasMissing && (
                            <Icon
                              className="iconTableSP"
                              svg={warningFillYellow}
                              width="0.75rem"
                              height="0.75rem"
                              onClick={() => this.showMissingFieldModal(label)}
                            />
                          )}
                          <span>{t("settings.sections.settingsWebmail.signature.field-" + label)}</span>
                        </div>
                      );
                    })}
                </div>

                <Toggle
                  className="inputFull2"
                  label={t("input.label.signature.apply.toNew")}
                  name="apply_signature_new_emails"
                  checked={formData.apply_signature_new_emails}
                  errors={formErrors.apply_signature_new_emails}
                  onChange={setFormFieldValue}
                  disabled={domain.plan.name === "basic"}
                />

                {/*actions*/}
                <div className="inputFull2 wrapInlineBtnSP">
                  <label className={cx("labelInlineBtnSP", { disabled: domain.plan.name === "basic" })}>
                    {t("input.label.signature.apply.toWhitout")}
                  </label>

                  <Button
                    color="outline"
                    type="button"
                    onClick={this.showApplyToMissing}
                    disabled={domain.plan.name === "basic"}
                  >
                    button.setSignature
                  </Button>
                </div>

                <div className="inputFull2 wrapInlineBtnSP">
                  <label className={cx("labelInlineBtnSP", { disabled: domain.plan.name === "basic" })}>
                    {t("input.label.signature.apply.toAll")}
                  </label>

                  <Button
                    type="button"
                    color="outline"
                    onClick={this.showApplyToAllModal}
                    disabled={domain.plan.name === "basic"}
                  >
                    button.setSignature
                  </Button>
                </div>
              </AvailabilityBox>
            </div>
          </div>

          {/* Company address book */}
          <div className="sectionSP">
            <div className="infoSectionSP">
              <div className="settingTitle">{t("settings.sections.settingsWebmailHideAddressbook.title")}</div>
              <div
                className="sectionDesc"
                dangerouslySetInnerHTML={{
                  __html: t("settings.sections.settingsWebmailHideAddressbook.desc"),
                }}
              ></div>
            </div>
            <div className="formsSectionSP">
              <Toggle
                className="inputFull2"
                label={t("input.label.hide_from_addressbook_company")}
                name="hide_from_addressbook_company"
                checked={formData.hide_from_addressbook_company}
                errors={formErrors.hide_from_addressbook_company}
                onChange={setFormFieldValue}
              />
            </div>
          </div>
        </div>
      </form>
    );
  }
}
